import { ServiceType } from '@/types/crm';
import { cn } from '@/lib/utils';
import { useOrganization } from '@/services/organizations';
import { useInquiryCategories } from '@/services/inquiryCategories';
import { iconMap, getDefaultServiceTabs, ServiceTabConfig } from '@/data/serviceTabPresets';
import { Inbox } from 'lucide-react';
import { NovaSpinner } from '@/components/nova';

interface ServiceTabsProps {
  activeTab: ServiceType;
  onTabChange: (tab: ServiceType) => void;
}

// System-level "All" tab - non-removable, non-editable, always first.
// Sentence case per ORBIT_DESIGN.md §3.3.
const ALL_TAB: ServiceTabConfig = {
  id: 'all',
  label: 'All inquiries',
  icon: 'inbox'
};

export const ServiceTabs = ({ activeTab, onTabChange }: ServiceTabsProps) => {
  const { data: organization, isLoading: loadingOrg } = useOrganization();
  const { data: inquiryCategories = [], isLoading: loadingCategories } = useInquiryCategories();

  // Build tabs list: Always start with "All" tab, then add category tabs
  const tabs: ServiceTabConfig[] = (() => {
    let categoryTabs: ServiceTabConfig[] = [];
    
    // Priority 1: Use inquiry_categories table if available
    if (inquiryCategories.length > 0) {
      categoryTabs = inquiryCategories.map(cat => ({
        id: cat.name.trim().toLowerCase().replace(/\s+/g, '_'),
        label: cat.name.trim(),
        icon: cat.icon || 'sparkles',
      }));
    }
    // Priority 2: Fall back to organization.service_tabs (legacy)
    else if (organization?.service_tabs) {
      categoryTabs = organization.service_tabs as ServiceTabConfig[];
    }
    // Priority 3: Default tabs
    else {
      categoryTabs = getDefaultServiceTabs();
    }
    
    // Always prepend the permanent "All" tab
    return [ALL_TAB, ...categoryTabs];
  })();

  const isLoading = loadingOrg || loadingCategories;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-3">
        <NovaSpinner size={20} />
      </div>
    );
  }

  return (
    <div className="flex gap-1 p-1 bg-secondary/50 rounded-xl border border-border overflow-x-auto">
      {tabs.map((tab) => {
        // Use Inbox icon for the "All" tab, otherwise use iconMap
        const Icon = tab.id === 'all' ? Inbox : (iconMap[tab.icon] || iconMap.sparkles);
        const isActive = activeTab === tab.id;
        
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              // No shadow per Mission Control discipline. Active uses
              // atomic-cream lift + foreground text (not orange) so we
              // stay under "max 2 oranges per screen".
              'flex items-center gap-2 px-5 py-3 rounded-lg font-medium text-sm transition-colors duration-150 whitespace-nowrap',
              isActive
                ? 'bg-card text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
            )}
          >
            <Icon className={cn('w-4 h-4', isActive && 'text-primary')} />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};
